import { IntegrationType } from '../entities/integrations/integration_type';
import {
  IntegrationOptionsRequest,
  IntegrationSetupRequest,
  RegisterDeviceRequest,
  ResGetIntegrations,
  ResGetIntegrationSettings,
  UpdateIntegrationSettingsRequest
} from '../types/integration-types';
import { BaseIntegrationEndpoints } from './base';

export class Integrations extends BaseIntegrationEndpoints {
  /**
   * Get all installed integrations for the user
   */
  async getInstalledIntegrations() {
    const result = await this.requestWithStatus<ResGetIntegrations>(
      'GET',
      '/api/integrations/installed'
    );
    return result;
  }

  /**
   * Get all installed admin integrations for the org
   */
  async getInstalledAdminIntegrations() {
    const result = await this.requestWithStatus<ResGetIntegrations>(
      'GET',
      '/api/integrations/admin-installed'
    );
    return result;
  }

  /**
   * Set up a third party integration
   * @param integrationType - Type of the integration we are setting up
   * @param body - information to store for the integration
   * @param isAdmin - Whether this is an admin integration. Example is microsoft-teams org setup
   * @returns
   */
  async setupIntegration(
    integrationType: IntegrationType,
    body: IntegrationSetupRequest,
    isAdmin: boolean = false
  ) {
    let url = `/${integrationType}/setup`;
    if (isAdmin) {
      url += '?userType=admin';
    }

    const result = await this.requestWithStatus('POST', url, body);
    return result;
  }

  /**
   * Save Integration Options
   * @param integrationType - Type of the integration we are modifying
   * @param body - information to store for the integration
   * @param isAdmin - Whether this is an admin integration. Example is google app 2 way syncing
   * @returns
   */
  async saveIntegrationOptions(
    integrationType: IntegrationType,
    body: IntegrationOptionsRequest,
    isAdmin: boolean = false
  ) {
    let url = `/api/integrations/${isAdmin ? 'admin-options' : 'options'}`;
    const result = await this.requestWithStatus('POST', url, {
      integrationType: integrationType,
      options: body
    });
    return result;
  }

  /**
   * Deauthorize an integration
   * @param integrationType - The type of integration, e.g. google or zoom
   * @param isAdmin - Whether this is an admin integration. Example is google 2 way sync admin
   */
  async deauthorizeIntegration(
    integrationType: IntegrationType,
    isAdmin: boolean = false
  ) {
    let url = `/${integrationType}/deauthorize`;
    if (isAdmin) {
      url += '?userType=admin';
    }

    const result = await this.requestWithStatus('DELETE', url, {});
    return result;
  }

  /**
   * Get all installed integration settings for the user
   */
  async getInstalledIntegrationSettings() {
    const result = await this.requestWithStatus<ResGetIntegrationSettings>(
      'GET',
      '/api/integrations/settings'
    );
    return result;
  }

  /**
   * Update installed integrations settings
   * @param settingsData - integrations and their updated settings
   */
  async updateInstalledIntegrationSettings(
    settingsData: UpdateIntegrationSettingsRequest
  ) {
    const result = await this.requestWithStatus<ResGetIntegrationSettings>(
      'PUT',
      '/api/integrations/settings',
      settingsData
    );
    return result;
  }

  /**
   * Registers a device token for push notifications
   * @param body - Request containing the device information
   * @returns
   */
  async registerDevice(body: RegisterDeviceRequest) {
    const result = await this.requestWithStatus(
      'POST',
      `/api/devices/register`,
      body
    );
    return result;
  }

  /**
   * Syncs resources with the freshwater system
   */
  async syncFreshwaterResources() {
    const result = await this.requestWithStatus(
      'POST',
      '/api/freshwater/sync',
      {}
    );
    return result;
  }
}
